import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Radio, RadioGroup, FormControl, Alert, TextField, Collapse } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { computeIsGermanDefault, getCurrentLangCode } from '../utils/language';

// ── Toggle promo code visibility ──────────────────────────────
// Set to false to hide the promo code section entirely
const SHOW_PROMO_CODE = true;

function getRestBase() {
  if (typeof window !== 'undefined' && window.rypLoginConfig && window.rypLoginConfig.restUrl) {
    return window.rypLoginConfig.restUrl;
  }
  return (window.location.origin || '') + '/?rest_route=/ryp/v1/';
}

function buildRestUrl(endpoint) {
  const base = getRestBase();
  if (base.includes('rest_route=')) {
    return base.replace(/\/$/, '').replace(/\/ryp\/v1\/?$/, '') + '/ryp/v1/' + endpoint;
  }
  return base + endpoint;
}

function isUiTextTestMode() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('ui_text_test') === '1';
  } catch (e) {
    return false;
  }
}

function getUiTextRestUrl(lang, page, mode, includeTestFlag) {
  const origin = window.location.origin || '';
  let url =
    origin +
    '/index.php?rest_route=/ryp/v1/ui-texts' +
    '&lang=' + encodeURIComponent(lang) +
    '&page=' + encodeURIComponent(page) +
    '&status=' + encodeURIComponent(mode || 'approved');
  if (includeTestFlag) url += '&ui_text_test=1';
  return url;
}

const products = [
  {
    id: 'standard',
    name: 'Standard Fit',
    nameDe: 'Standard Produkt',
    description: '',
    descriptionDe: '',
    price: '100.00',
    currency: 'CHF',
    available: true
  },
  {
    id: 'premium',
    name: 'Premium Fit',
    nameDe: 'Premium Produkt',
    description: '',
    descriptionDe: '',
    price: '200.00',
    currency: 'CHF',
    available: false,
    comingSoon: true
  }
];

export default function ProductChoice({ setCurrentStep }) {
  const [selectedProduct, setSelectedProduct] = useState('standard');
  const [isGerman, setIsGerman] = useState(() => computeIsGermanDefault());
  const [uiTexts, setUiTexts] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoStatus, setPromoStatus] = useState(null); // null | 'valid' | 'invalid'
  const [promoMessage, setPromoMessage] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null); // { percent, amount, finalPrice }
  const isPolish = getCurrentLangCode() === 'pl';

  useEffect(() => {
    // Language (safe): German default, English only via ?lang=en
    setIsGerman(computeIsGermanDefault());
  }, []);

  useEffect(() => {
    let active = true;
    const lang = getCurrentLangCode();
    const includeTestFlag = isUiTextTestMode();
    const mode = includeTestFlag ? 'draft' : 'approved';
    const url = getUiTextRestUrl(lang, 'product_choice', mode, includeTestFlag);

    fetch(url, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!active) return;
        if (!data || !data.success || !data.texts || typeof data.texts !== 'object') {
          setUiTexts({});
          return;
        }
        setUiTexts(data.texts);
      })
      .catch(() => {
        if (active) setUiTexts({});
      });

    return () => {
      active = false;
    };
  }, [isGerman, isPolish]);

  useEffect(() => {
    // Entering product selection should not inherit stale promo values from old sessions.
    localStorage.removeItem('promoCode');
    localStorage.removeItem('promoDiscount');
  }, []);

  useEffect(() => {
    // Promo code validity is tied to selected product/base price.
    // Reset promo whenever product selection changes.
    setPromoCode('');
    setPromoStatus(null);
    setPromoMessage('');
    setPromoDiscount(null);
    localStorage.removeItem('promoCode');
    localStorage.removeItem('promoDiscount');
  }, [selectedProduct]);

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;

    try {
      const selectedPrice = products.find(p => p.id === selectedProduct)?.price || '100.00';
      const url = buildRestUrl('validate-promo');
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, product: selectedProduct, price: selectedPrice }),
      });
      const data = await response.json();

      if (data.valid) {
        setPromoStatus('valid');
        setPromoMessage(
          isPolish
            ? `${data.percent}% rabatu — oszczędzasz CHF ${data.discount.toFixed(2)}`
            : (isGerman
              ? `${data.percent}% Rabatt — Sie sparen CHF ${data.discount.toFixed(2)}`
              : `${data.percent}% off — you save CHF ${data.discount.toFixed(2)}`)
        );
        const discountData = {
          percent: data.percent,
          amount: data.discount,
          finalPrice: data.finalPrice,
          product: selectedProduct,
          basePrice: parseFloat(selectedPrice),
          code: code,
        };
        setPromoDiscount(discountData);
        localStorage.setItem('promoCode', code);
        localStorage.setItem('promoDiscount', JSON.stringify(discountData));
      } else {
        setPromoStatus('invalid');
        setPromoMessage(isPolish ? 'Nieprawidłowy kod promocyjny' : (isGerman ? 'Ungültiger Promo-Code' : 'Invalid promo code'));
        setPromoDiscount(null);
        localStorage.removeItem('promoCode');
        localStorage.removeItem('promoDiscount');
      }
    } catch (err) {
      setPromoStatus('invalid');
      setPromoMessage(isPolish ? 'Błąd podczas weryfikacji' : (isGerman ? 'Fehler bei der Überprüfung' : 'Error validating code'));
      setPromoDiscount(null);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoStatus(null);
    setPromoMessage('');
    setPromoDiscount(null);
    localStorage.removeItem('promoCode');
    localStorage.removeItem('promoDiscount');
  };

  const handleContinue = () => {
    // Store selected product in localStorage
    localStorage.setItem('selectedProduct', selectedProduct);
    localStorage.setItem('selectedProductPrice', products.find(p => p.id === selectedProduct).price);
    if (promoStatus === 'valid' && promoDiscount) {
      // Keep exact user-entered case (backend promo validation is case-sensitive).
      localStorage.setItem('promoCode', promoCode.trim());
      localStorage.setItem('promoDiscount', JSON.stringify(promoDiscount));
    } else {
      // Avoid carrying stale discount into payment step.
      localStorage.removeItem('promoCode');
      localStorage.removeItem('promoDiscount');
    }
    
    // Move to client details and payment step
    setCurrentStep({ activeStep: 3, status: 'product-selected' });
  };

  const getText = (key) => {
    if (isPolish) {
      const polishTexts = {
        title: 'Wybór produktu',
        subtitle: 'Wybierz pożądany produkt, aby porównać swoją obecną instytucję finansową',
        back: 'Wstecz',
        continue: 'Dalej',
        comingSoon: 'Wkrótce dostępne',
        dynamicPrices: 'Ceny dynamiczne: x.yz',
        promoToggle: 'Masz kod promocyjny?',
        promoPlaceholder: 'Wpisz kod promocyjny',
        promoApply: 'Zastosuj',
        promoRemove: 'Usuń'
      };
      const fallback = polishTexts[key] || key;
      const uiValue = uiTexts && uiTexts[`product_choice.${key}`];
      if (uiValue !== undefined && uiValue !== null && String(uiValue).trim() !== '') return String(uiValue).trim();
      return fallback;
    }
    if (isGerman) {
      const germanTexts = {
        title: 'Produktauswahl',
        subtitle: 'Wählen Sie das gewünschte Produkt, um Ihr momentanes Finanzinstitut zu vergleichen.',
        back: 'Zurück',
        continue: 'Weiter',
        comingSoon: 'Bald verfügbar',
        dynamicPrices: 'Dynamic prices: x.yz',
        promoToggle: 'Haben Sie einen Promo-Code?',
        promoPlaceholder: 'Promo-Code eingeben',
        promoApply: 'Anwenden',
        promoRemove: 'Entfernen'
      };
      const fallback = germanTexts[key] || key;
      const uiValue = uiTexts && uiTexts[`product_choice.${key}`];
      if (uiValue !== undefined && uiValue !== null && String(uiValue).trim() !== '') return String(uiValue).trim();
      return fallback;
    }
    const englishTexts = {
      title: 'Product Choice',
      subtitle: 'Select your desired product to compare your current financial institution',
      back: 'Back',
      continue: 'Continue',
      comingSoon: 'Coming Soon',
      dynamicPrices: 'Dynamic prices: x.yz',
      promoToggle: 'Have a promo code?',
      promoPlaceholder: 'Enter promo code',
      promoApply: 'Apply',
      promoRemove: 'Remove'
    };
    const fallback = englishTexts[key] || key;
    const uiValue = uiTexts && uiTexts[`product_choice.${key}`];
    if (uiValue !== undefined && uiValue !== null && String(uiValue).trim() !== '') return String(uiValue).trim();
    return fallback;
  };

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => setCurrentStep({ activeStep: 1, status: 'back-from-product' })}
          sx={{ textTransform: 'none' }}
        >
          {getText('back')}
        </Button>
      </Box>

      <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
        {getText('title')}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {getText('subtitle')}
      </Typography>

      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <RadioGroup
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          sx={{ gap: 2 }}
        >
          {products.map((product) => (
            <Card
              key={product.id}
              sx={{
                border: selectedProduct === product.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                cursor: product.available ? 'pointer' : 'not-allowed',
                opacity: product.available ? 1 : 0.6,
                transition: 'all 0.3s ease',
                '&:hover': product.available ? {
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                } : {}
              }}
              onClick={() => product.available && setSelectedProduct(product.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Radio
                    checked={selectedProduct === product.id}
                    value={product.id}
                    disabled={!product.available}
                    sx={{ mt: -1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          variant="h6"
                          component="h3"
                          noWrap
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {product.id === 'standard'
                            ? (isPolish ? 'Produkt standardowy: 100 CHF' : (isGerman ? 'Standard Produkt: 100 CHF' : 'Standard Fit: 100 CHF'))
                            : (isPolish ? 'Produkt premium' : (isGerman ? 'Premium Produkt' : 'Premium Fit'))}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.85rem' }}
                        >
                          {product.id === 'standard'
                            ? (isPolish
                              ? 'Możliwe zmiany ceny np. przez rabaty'
                              : (isGerman
                                ? 'Preisänderungen z.B. durch Rabatte möglich'
                                : 'Changes in price possible e.g. by vouchers'))
                            : (isPolish
                              ? 'Możliwe zmiany ceny np. przez rabaty'
                              : (isGerman
                                ? 'Preisänderungen z.B. durch Rabatte möglich'
                                : 'Changes in price possible e.g. by vouchers'))}
                        </Typography>
                      </Box>
                      {selectedProduct === product.id && product.available && (
                        <CheckCircleIcon color="primary" />
                      )}
                    </Box>
                    {product.comingSoon && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>
                          {getText('comingSoon')}
                        </Alert>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
      </FormControl>

      {/* ── Promo Code Section ── */}
      {SHOW_PROMO_CODE && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<LocalOfferIcon />}
            onClick={() => setPromoOpen(!promoOpen)}
            sx={{ textTransform: 'none', color: '#666', fontSize: '0.9rem' }}
          >
            {getText('promoToggle')}
          </Button>
          <Collapse in={promoOpen}>
            <Card sx={{ mt: 1, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
              <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    placeholder={getText('promoPlaceholder')}
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.replace(/[^a-zA-Z0-9\-_]/g, ''));
                      if (promoStatus) { setPromoStatus(null); setPromoMessage(''); }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo(); }}
                    disabled={promoStatus === 'valid'}
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 30 }}
                  />
                  {promoStatus === 'valid' ? (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleRemovePromo}
                      sx={{ textTransform: 'none', minWidth: 90, height: 40 }}
                    >
                      {getText('promoRemove')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyPromo}
                      disabled={!promoCode.trim()}
                      sx={{ textTransform: 'none', minWidth: 90, height: 40 }}
                    >
                      {getText('promoApply')}
                    </Button>
                  )}
                </Box>
                {promoStatus === 'valid' && promoMessage && (
                  <Alert severity="success" sx={{ mt: 1.5, py: 0, fontSize: '0.85rem' }}>
                    {promoMessage}
                  </Alert>
                )}
                {promoStatus === 'invalid' && promoMessage && (
                  <Alert severity="error" sx={{ mt: 1.5, py: 0, fontSize: '0.85rem' }}>
                    {promoMessage}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Collapse>
        </Box>
      )}

      {promoStatus === 'valid' && promoDiscount && (
        <Card sx={{ mt: 3, border: '1px solid #4caf50', backgroundColor: '#f1f8e9' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isPolish ? 'Cena pierwotna' : (isGerman ? 'Originalpreis' : 'Original price')}
              </Typography>
              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999' }}>
                CHF {products.find(p => p.id === selectedProduct)?.price}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="success.main">
                {isPolish ? 'Rabat' : (isGerman ? 'Rabatt' : 'Discount')} ({promoDiscount.percent}%)
              </Typography>
              <Typography variant="body2" color="success.main">
                − CHF {promoDiscount.amount.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, pt: 0.5, borderTop: '1px solid #c8e6c9' }}>
              <Typography variant="body1" fontWeight={700}>
                {isPolish ? 'Cena końcowa' : (isGerman ? 'Endpreis' : 'Final price')}
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary">
                CHF {promoDiscount.finalPrice.toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={!selectedProduct}
          sx={{ minWidth: 200, textTransform: 'none' }}
        >
          {getText('continue')}
        </Button>
      </Box>
    </Box>
  );
}
